import { config } from '../config/config';
import { supabase } from '../lib/supabase';

// Interface pour les détails de paiement
export interface CampayShopPaymentDetails {
  amount: number;
  phoneNumber: string;
  operator: string;
  order_id: string;
  order_number: string;
  description?: string;
  customerName?: string;
  customerEmail?: string;
}

// Interface pour la réponse d'initiation de paiement
export interface CampayShopPaymentResponse {
  transaction: {
    id: string;
    amount: number;
    status: string;
    payment_method: string;
    payment_reference: string | null;
    payment_url: string | null;
  };
  payment_reference: string;
  payment_url: string;
}

// Interface pour la réponse de statut de paiement
export interface CampayShopStatusResponse {
  status: 'success' | 'pending' | 'failed' | 'SUCCESSFUL' | 'PENDING' | 'FAILED';
  data: any;
}

/**
 * Initie un paiement via la fonction Edge Campay spécifique pour la boutique
 */
export const initiateCampayShopEdgePayment = async (
  details: CampayShopPaymentDetails
): Promise<CampayShopPaymentResponse> => {
  try {
    // Utiliser la nouvelle fonction edge spécifique pour la boutique
    const response = await fetch(`${config.supabaseUrl}/functions/v1/shop-campay-initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify(details),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Échec de l'initiation du paiement");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Erreur lors de l'initiation du paiement Campay:", error);
    throw error;
  }
};

/**
 * Vérifie le statut d'un paiement via la base de données pour la boutique
 */
export const checkCampayShopEdgePaymentStatus = async (
  reference: string,
  order_id: string
): Promise<CampayShopStatusResponse> => {
  const requestId = Math.random().toString(36).substring(2, 10);
  console.log(`[CampayShopEdge][${requestId}] Début de checkCampayShopEdgePaymentStatus - reference: ${reference}, order_id: ${order_id}`);
  
  try {
    // Vérifier le statut de la commande dans la base de données
    console.log(`[CampayShopEdge][${requestId}] Vérification du statut dans la base de données pour la commande ${order_id}`);
    
    const startTime = Date.now();
    const { data: order, error } = await supabase
      .from('shop_orders')
      .select('status, payment_reference, created_at')
      .eq('id', order_id)
      .single();
    
    const queryTime = Date.now() - startTime;
    console.log(`[CampayShopEdge][${requestId}] Requête Supabase terminée en ${queryTime}ms`);
    console.log(`[CampayShopEdge][${requestId}] Résultat de la requête:`, { 
      order: order ? {
        status: order.status,
        payment_reference: order.payment_reference,
        created_at: order.created_at
      } : null, 
      error: error ? error.message : null 
    });
    
    if (error) {
      console.error(`[CampayShopEdge][${requestId}] Erreur lors de la récupération de la commande:`, error);
      throw new Error(`Erreur lors de la récupération de la commande: ${error.message}`);
    }
    
    if (!order) {
      console.error(`[CampayShopEdge][${requestId}] Commande ${order_id} introuvable`);
      throw new Error('Commande introuvable');
    }
    
    console.log(`[CampayShopEdge][${requestId}] Statut de la commande ${order_id} dans la base de données: ${order.status}`);
    
    // Vérifier si la commande est marquée comme payée
    if (order.status === 'paid') {
      console.log(`[CampayShopEdge][${requestId}] La commande ${order_id} est déjà marquée comme payée, retour immédiat avec succès`);
      return { 
        status: 'success', 
        data: { 
          order_id,
          payment_reference: reference,
          status: 'paid',
          created_at: order.created_at
        } 
      };
    }
    
    console.log(`[CampayShopEdge][${requestId}] La commande ${order_id} n'est pas encore payée (statut: ${order.status}), vérification via la fonction edge`);
    
    // Si la commande n'est pas encore payée, vérifier via la fonction edge
    console.log(`[CampayShopEdge][${requestId}] Appel à la fonction edge campay-shop-check`);
    const fetchStartTime = Date.now();
    const response = await fetch(`${config.supabaseUrl}/functions/v1/campay-shop-check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify({
        reference,
        order_id
      }),
    });

    const fetchTime = Date.now() - fetchStartTime;
    console.log(`[CampayShopEdge][${requestId}] Réponse de la fonction edge - status: ${response.status}, temps: ${fetchTime}ms`);
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error(`[CampayShopEdge][${requestId}] Échec de la vérification du statut via la fonction edge:`, errorData);
      throw new Error(errorData.error || "Échec de la vérification du statut du paiement");
    }

    const data = await response.json();
    console.log(`[CampayShopEdge][${requestId}] Données reçues de la fonction edge:`, JSON.stringify(data));
    
    // Vérifier à nouveau le statut de la commande après l'appel à la fonction edge
    console.log(`[CampayShopEdge][${requestId}] Vérification finale du statut dans la base de données`);
    const { data: finalOrder } = await supabase
      .from('shop_orders')
      .select('status')
      .eq('id', order_id)
      .single();
      
    console.log(`[CampayShopEdge][${requestId}] Statut final de la commande: ${finalOrder?.status || 'inconnu'}`);
    
    return data;
  } catch (error) {
    console.error(`[CampayShopEdge][${requestId}] Erreur lors de la vérification du statut du paiement:`, error);
    throw error;
  }
};
